import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Card } from '@/components/card/card';
import { DataGrid } from '@/components/data-grid';
import { createColumnHelper } from '@tanstack/react-table';
import { KeenIcon } from '@/components';
import usePagination from '@/hooks/usePagination';
import { useSnackbar } from 'notistack';
import { SubscriptionsService } from '@/services/subscriptions/subscriptionsService';
import { SubscriptionStatus } from '@/constant';
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalTitle,
} from '@/components/modal';
import { Button } from '@/components/button/Buttons';
import { usePermissions } from '@/hooks/usePermissions';
import { formatDisplayDate } from '@/utils/Date';
import type {
  ISubscription,
  ISubscriptionUser,
} from '@/types/subscription-management.types';
import CancelSubscriptionModal from '@/components/subscription-management/CancelSubscriptionModal';

interface SubscriptionUserListingsProps {
  refreshTrigger?: number;
}

const SubscriptionUserListings: React.FC<SubscriptionUserListingsProps> = ({
  refreshTrigger,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { hasPermission } = usePermissions();
  const [subscriptions, setSubscriptions] = useState<ISubscription[]>([]);
  const [selectedSubscriptionId, setSelectedSubscriptionId] =
    useState<string>('');
  const [users, setUsers] = useState<ISubscriptionUser[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [dataKey, setDataKey] = useState(() => Date.now());
  const [changeModalOpen, setChangeModalOpen] = useState(false);
  const [userToChange, setUserToChange] = useState<ISubscriptionUser | null>(
    null
  );
  const [selectedPlanId, setSelectedPlanId] = useState<string>('');
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [selectedSubscriptionType, setSelectedSubscriptionType] = useState<
    number | undefined
  >(undefined);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [userToCancel, setUserToCancel] = useState<ISubscriptionUser | null>(
    null
  );

  const getErrorMessage = (error: unknown, fallback: string): string => {
    const e = error as {
      response?: { data?: { message?: string } };
      message?: string;
    };
    return e?.response?.data?.message || e?.message || fallback;
  };

  const {
    totalItems,
    handleTotalItems,
    totalPages,
    handleTotalPages,
    debouncedValue,
    handleSearch,
    searchTerm,
    pagination,
    setPagination,
  } = usePagination();

  useEffect(() => {
    setPagination((prev) => ({ ...prev, pageSize: 10 }));
  }, [setPagination]);

  const columnHelper = createColumnHelper<ISubscriptionUser>();

  // Convert HTML string to plain text by stripping tags and decoding entities
  const toPlainText = (html: unknown): string => {
    if (typeof html !== 'string') return '';
    const div = document.createElement('div');
    div.innerHTML = html;
    return (div.textContent || div.innerText || '').trim();
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: 'Name',
        cell: (info) => {
          const displayName =
            (info.getValue() as string) || info.row.original.email || '-';
          return (
            <div className="flex items-center gap-3">
              <img
                src={
                  info.row.original.profile_path ||
                  '/media/avatars/defult_avtar.png'
                }
                alt={displayName || 'Subscriber'}
                className="rounded-full size-8 shrink-0"
                onError={(e) => {
                  (e.target as HTMLImageElement).src =
                    '/media/avatars/defult_avtar.png';
                }}
              />
              <span className="font-medium text-gray-900">{displayName}</span>
            </div>
          );
        },
      }),
      columnHelper.accessor('email', {
        header: 'Email',
        cell: (info) => (
          <div className="text-gray-800">{info.getValue() || '-'}</div>
        ),
      }),
      columnHelper.accessor('subscription_name', {
        header: 'Plan',
        cell: (info) => {
          const text = toPlainText(info.getValue());
          return <div className="text-gray-700">{text || '-'}</div>;
        },
      }),
      columnHelper.accessor('roles', {
        header: 'Roles',
        cell: (info) =>
          Array.isArray(info.getValue()) && info.getValue()?.length
            ? info.getValue()?.join(', ')
            : '-',
      }),
      columnHelper.accessor('status', {
        header: 'Status',
        cell: (info) => {
          const rawValue = info.getValue();

          const numericValue =
            typeof rawValue === 'string'
              ? Number.isNaN(parseInt(rawValue, 10))
                ? undefined
                : parseInt(rawValue, 10)
              : (rawValue as number | undefined);

          let label = '-';

          switch (numericValue) {
            case SubscriptionStatus.ACTIVE:
              label = 'Active';
              break;
            case SubscriptionStatus.INACTIVE:
              label = 'Inactive';
              break;
            case SubscriptionStatus.EXPIRED:
              label = 'Expired';
              break;
            case SubscriptionStatus.CANCELLED:
              label = 'Cancelled';
              break;
            default:
              if (
                rawValue !== null &&
                rawValue !== undefined &&
                rawValue !== ''
              ) {
                label = String(rawValue);
              }
          }

          return <div className="text-gray-700">{label}</div>;
        },
      }),
      columnHelper.accessor('subscribed_at', {
        header: 'Subscribed At',
        cell: (info) =>
          info.getValue() ? (
            <div className="text-gray-600">
              {formatDisplayDate(new Date(info.getValue() as string))}
            </div>
          ) : (
            <span>-</span>
          ),
      }),
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }: { row: { original: ISubscriptionUser } }) => {
          const currentSubscription = subscriptions.find(
            (sub) => sub.id === (row.original.subscription_id || '')
          );
          const isPaidPlan = (currentSubscription?.price ?? 0) > 0;

          return (
          <div className="flex items-center gap-2">
            {hasPermission('subscription_management_update') && (
              <>
              <button
                type="button"
                onClick={() => {
                  setUserToChange(row.original);
                  setSelectedPlanId(row.original.subscription_id || '');
                  setSelectedSubscriptionType(
                    (currentSubscription?.subscription_type as
                      | number
                      | undefined) ?? undefined
                  );
                  setChangeModalOpen(true);
                }}
                className="btn btn-sm btn-icon btn-light btn-outline"
                title="Change Plan"
                aria-label={`Change plan for ${row.original.email || row.original.name || 'subscriber'}`}
              >
                <KeenIcon
                  type="filled"
                  icon="pencil"
                  className="text-gray-500"
                />
              </button>
              {isPaidPlan && (
                <button
                  type="button"
                  onClick={() => {
                    setUserToCancel(row.original);
                    setCancelModalOpen(true);
                  }}
                  className="btn btn-sm btn-icon btn-light btn-outline"
                  title="Cancel Plan"
                  aria-label={`Cancel plan for ${row.original.email || row.original.name || 'subscriber'}`}
                >
                  <KeenIcon
                    type="filled"
                    icon="cross-circle"
                    className="text-red-500"
                  />
                </button>
              )}
              </>
            )}
          </div>
          );
        },
        enableSorting: false,
        meta: {
          className: 'w-[80px]',
        },
      },
    ],
    [columnHelper, hasPermission, subscriptions]
  );

  const fetchSubscriptions = useCallback(async () => {
    try {
      const response = await SubscriptionsService.getSubscriptions(1, 100, '');
      if (response.subscriptions && Array.isArray(response.subscriptions)) {
        setSubscriptions(response.subscriptions);
      } else {
        setSubscriptions([]);
      }
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(
        error,
        'Failed to load subscriptions'
      );
      enqueueSnackbar(errorMessage, { variant: 'error' });
      setSubscriptions([]);
    }
  }, [enqueueSnackbar]);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await SubscriptionsService.getSubscriptionUsers(
        selectedSubscriptionId || undefined,
        pagination.pageIndex + 1,
        pagination.pageSize,
        debouncedValue
      );
      const items = response.users || [];
      setUsers(items);
      const total = response.totalCount || items.length;
      handleTotalItems(total);
      handleTotalPages(Math.ceil(total / pagination.pageSize));
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error, 'Failed to load entities');
      enqueueSnackbar(errorMessage, { variant: 'error' });
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [
    selectedSubscriptionId,
    pagination.pageIndex,
    pagination.pageSize,
    debouncedValue,
    enqueueSnackbar,
    handleTotalItems,
    handleTotalPages,
  ]);

  useEffect(() => {
    fetchSubscriptions();
  }, [fetchSubscriptions]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    if (refreshTrigger && refreshTrigger > 0) {
      fetchSubscriptions();
      fetchUsers();
    }
  }, [refreshTrigger, fetchSubscriptions, fetchUsers]);

  const fetchRef = useRef(fetchUsers);
  useEffect(() => {
    fetchRef.current = fetchUsers;
  }, [fetchUsers]);
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchRef.current();
        setDataKey(Date.now());
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () =>
      document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  const handleCloseChangeModal = useCallback(() => {
    if (isSubscribing) return;
    setChangeModalOpen(false);
    setUserToChange(null);
    setSelectedPlanId('');
    setSelectedSubscriptionType(undefined);
  }, [isSubscribing]);

  const handleConfirmChange = useCallback(async () => {
    if (!userToChange) return;
    if (!selectedPlanId) {
      enqueueSnackbar('Please select a subscription plan', {
        variant: 'error',
      });
      return;
    }
    try {
      setIsSubscribing(true);
      await SubscriptionsService.subscribeUser(userToChange.id, selectedPlanId);
      enqueueSnackbar('Subscription has been updated successfully', {
        variant: 'success',
      });
      handleCloseChangeModal();
      await fetchUsers();
    } catch (error: unknown) {
      const msg = getErrorMessage(error, 'Failed to change plan');
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setIsSubscribing(false);
    }
  }, [
    userToChange,
    selectedPlanId,
    enqueueSnackbar,
    handleCloseChangeModal,
    fetchUsers,
  ]);

  const filteredSubscriptionsForModal = useMemo(
    () =>
      selectedSubscriptionType
        ? subscriptions.filter(
            (sub) =>
              (sub.subscription_type as number | undefined) ===
              selectedSubscriptionType
          )
        : subscriptions,
    [subscriptions, selectedSubscriptionType]
  );

  return (
    <Card className="subscription-user-listings overflow-hidden border-1 shadow-[0px 3px 4px 0px rgba(0, 0, 0, 0.03)] card border-gray-200 border rounded-[0.75rem]">
      <div className="card-header flex flex-wrap items-center gap-2 sm:gap-4 lg:gap-6 justify-between px-4 py-3 border-b border-gray-200">
        <h2 className="card-title text-lg font-semibold text-gray-900">
          Entities by Subscription
        </h2>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2">
            <label
              htmlFor="subscription-select"
              className="text-sm text-gray-700"
            >
              Plan
            </label>
            <select
              id="subscription-select"
              className="select select-sm h-8 sm:h-10 min-w-[220px] border-gray-300 rounded-md"
              value={selectedSubscriptionId}
              onChange={(e) => {
                setSelectedSubscriptionId(e.target.value);
                setPagination((prev) => ({ ...prev, pageIndex: 0 }));
              }}
            >
              <option value="">All Plans</option>
              {subscriptions.length === 0 && (
                <option value="" disabled>
                  No plans available
                </option>
              )}
              {subscriptions.map((sub) => (
                <option key={sub.id} value={sub.id}>
                  {toPlainText(sub.name)}
                </option>
              ))}
            </select>
          </div>
          <div
            className="flex-shrink-0"
            role="search"
            aria-label="Search entities"
          >
            <label
              className="input input-sm h-8 sm:h-10 flex items-center"
              htmlFor="search-users-by-subscription"
              aria-label="Search users by subscription"
            >
              <KeenIcon
                type="filled"
                icon="magnifier"
                className="text-gray-500"
              />
              <input
                id="search-users-by-subscription"
                placeholder="Search entities..."
                type="text"
                value={searchTerm}
                onChange={handleSearch}
                className="w-full sm:w-56 h-8 sm:h-10 text-sm"
                aria-label="Search entities by name or email"
                role="searchbox"
              />
            </label>
          </div>
        </div>
      </div>
      {loading ? (
        <div className="text-center py-4" role="status" aria-live="polite">
          Loading entities...
        </div>
      ) : users?.length === 0 ? (
        <div className="text-center py-4" role="alert">
          No entities found for this plan
        </div>
      ) : (
        <div className="subscription-users-grid-wrapper">
          <DataGrid
            key={dataKey}
            columns={columns}
            data={users}
            loading={loading}
            rowSelect={false}
            aria-label="Entities by subscription table"
            pagination={{
              page: pagination.pageIndex,
              size: pagination.pageSize,
              more: true,
              totalItems: totalItems,
              totalPages: totalPages,
              setPagination,
              info: `{from} - {to} of {count}`,
              sizes: [5, 10, 25, 50, 100],
              sizesLabel: 'Show',
              sizesDescription: 'per page',
            }}
            serverSide={true}
          />
        </div>
      )}
      {/* Change Plan Modal */}
      <Modal open={changeModalOpen} onClose={handleCloseChangeModal}>
        <ModalContent
          className="max-w-md mx-auto mt-20"
          role="dialog"
          aria-modal="true"
          aria-labelledby="change-plan-title"
          aria-describedby="change-plan-description"
        >
          <ModalHeader>
            <ModalTitle id="change-plan-title">Change Plan</ModalTitle>
            <button
              onClick={handleCloseChangeModal}
              className="text-gray-400 hover:text-gray-600"
              type="button"
              aria-label="Close"
              disabled={isSubscribing}
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </ModalHeader>
          <ModalBody>
            <div className="space-y-4">
              {/* ARIA: Descriptive text referenced by aria-describedby */}
              <p className="text-sm text-gray-600" id="change-plan-description">
                Select a subscription plan to assign to{' '}
                <span className="font-medium">
                  {userToChange?.email ||
                    userToChange?.name ||
                    'this subscriber'}
                </span>
                .
              </p>
              <div className="flex items-center gap-3">
                <label
                  htmlFor="change-plan-select"
                  className="text-sm text-gray-700 min-w-16"
                >
                  Plan
                </label>
                <select
                  id="change-plan-select"
                  className="select select-sm h-10 w-full border-gray-300 rounded-md"
                  value={selectedPlanId}
                  onChange={(e) => setSelectedPlanId(e.target.value)}
                  disabled={isSubscribing}
                >
                  <option value="" disabled>
                    Select a plan
                  </option>
                  {filteredSubscriptionsForModal.map((sub) => (
                    <option key={sub.id} value={sub.id}>
                      {toPlainText(sub.name)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleCloseChangeModal}
                  disabled={isSubscribing}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  onClick={handleConfirmChange}
                  disabled={isSubscribing || !selectedPlanId}
                  aria-label="Confirm change plan"
                >
                  {isSubscribing ? 'Updating...' : 'Change Plan'}
                </Button>
              </div>
            </div>
          </ModalBody>
        </ModalContent>
      </Modal>

      <CancelSubscriptionModal
        isOpen={cancelModalOpen}
        onClose={() => {
          setCancelModalOpen(false);
          setUserToCancel(null);
        }}
        entityType="boater"
        entityId={userToCancel?.id || ''}
        entityName={userToCancel?.email || userToCancel?.name}
        onSuccess={() => {
          fetchUsers();
        }}
      />
    </Card>
  );
};

export default React.memo(SubscriptionUserListings);
